/**
 * Refresh-resume marker (2026-08-11).
 *
 * While an answer is streaming, the client records which conversation /
 * thread / assistant bubble the stream belongs to. A page reload (or SPA
 * navigation that unmounts the workspace) aborts the SSE request, but the
 * backend keeps the pipeline running and its stream re-attachable — so on
 * the next mount the app reads this marker, probes GET /api/v1/chat/active,
 * and either re-attaches with a full replay or recovers the persisted turn.
 *
 * sessionStorage on purpose (SEC-04): same PHI posture as the transcript
 * cache — per tab, gone when the tab closes, purged on logout alongside the
 * thread store.
 */

const STORAGE_KEY = 'march_active_stream_v1';

/** Streams older than this are not worth resuming (matches the client's max stream duration). */
const DEFAULT_MAX_AGE_MS = 1_800_000;

export interface ActiveStreamRecord {
  conversationId: string;
  threadId: string;
  assistantId: string;
  patientId: string;
  command: string;
  startedAt: number;
}

function storageArea(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * True once the page has started unloading (refresh / navigation / tab close).
 *
 * This is the crux of the resume fix (dev finding 2026-08-11, second root
 * cause). A page unload ABORTS the in-flight SSE fetch, which surfaces to
 * `openCommandStream` as a network error with `closed === false` — nobody
 * called close() — so the stream's error path runs during teardown and used to
 * delete the resume marker: the exact record the reloading page needs to find
 * its interrupted answer. The deletion persists to sessionStorage, so the new
 * page came up with nothing to resume from and silently gave up.
 *
 * Proven directly on dev: a marker planted with no live stream survived a
 * reload; a marker written while a stream was live did not.
 *
 * A teardown-time "the stream ended" is never real information about the run —
 * the run keeps going server-side — so during unload the marker must be left
 * exactly as it is. Logout still clears it via `force`.
 */
let unloading = false;

if (typeof window !== 'undefined') {
  // pagehide covers bfcache and iOS Safari, where beforeunload does not fire.
  window.addEventListener('pagehide', () => {
    unloading = true;
  });
  window.addEventListener('beforeunload', () => {
    unloading = true;
  });
}

export function isPageUnloading(): boolean {
  return unloading;
}

export function recordActiveStream(record: ActiveStreamRecord): void {
  try {
    storageArea()?.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // best-effort: resume is an enhancement, never a blocker
  }
}

/**
 * Clear the marker. When `conversationId` is given, only a marker for that
 * conversation is cleared — so finishing stream A never erases the marker a
 * newer stream B just wrote.
 *
 * Ignored while the page is unloading (see `isPageUnloading`): a stream that
 * "ends" because the tab is going away says nothing about the run, and
 * dropping the marker there is precisely what broke refresh-resume. Pass
 * `{ force: true }` for teardown that genuinely must wipe it (logout).
 */
export function clearActiveStream(
  conversationId?: string,
  opts?: { force?: boolean },
): void {
  const store = storageArea();
  if (!store) return;
  if (unloading && !opts?.force) return;
  try {
    if (conversationId) {
      const raw = store.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<ActiveStreamRecord>;
        if (parsed?.conversationId && parsed.conversationId !== conversationId) return;
      }
    }
    store.removeItem(STORAGE_KEY);
  } catch {
    try {
      store.removeItem(STORAGE_KEY);
    } catch {
      // best-effort
    }
  }
}

/** Read a still-fresh marker, or null. Stale/corrupt markers are removed. */
export function readActiveStream(maxAgeMs: number = DEFAULT_MAX_AGE_MS): ActiveStreamRecord | null {
  const store = storageArea();
  if (!store) return null;
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ActiveStreamRecord>;
    const fresh =
      typeof parsed?.conversationId === 'string' &&
      parsed.conversationId.trim() !== '' &&
      typeof parsed?.threadId === 'string' &&
      typeof parsed?.assistantId === 'string' &&
      typeof parsed?.startedAt === 'number' &&
      Date.now() - parsed.startedAt < maxAgeMs;
    if (!fresh) {
      store.removeItem(STORAGE_KEY);
      return null;
    }
    return {
      conversationId: parsed.conversationId as string,
      threadId: parsed.threadId as string,
      assistantId: parsed.assistantId as string,
      patientId: typeof parsed.patientId === 'string' ? parsed.patientId : '',
      command: typeof parsed.command === 'string' ? parsed.command : '',
      startedAt: parsed.startedAt as number,
    };
  } catch {
    try {
      store.removeItem(STORAGE_KEY);
    } catch {
      // best-effort
    }
    return null;
  }
}
